import Immutable from "immutable";
import { all, takeEvery, takeLatest, call, select, put, take } from "redux-saga/effects";

import * as actions from "state/school_money/actions";
import {
  getSchoolBasketItems,
  isStudentsReady,
  getStudentIDList,
  getBasketItems,
  getBasketItemsIssuesByItemType
} from "state/school_money/selectors";

import * as clubBooking from "state/school_money/sagas/basketSaga/clubBooking";
import * as dinnerBooking from "state/school_money/sagas/basketSaga/dinnerBooking";
import * as payment from "state/school_money/sagas/basketSaga/payment";
import * as shopItem from "state/school_money/sagas/basketSaga/shopItem";
import * as creditTopUp from "state/school_money/sagas/basketSaga/creditTopUp";

/**
 * Root saga - Attach the action listeners
 */
export default function* basketSaga() {
  yield all([
    
    takeEvery(actions.CLUB_BOOKING_ADD_TO_BASKET, clubBooking.addToBasket),
    takeEvery(actions.CLUB_BOOKING_REMOVE_FROM_BASKET, clubBooking.removeFromBasket),
    
    takeEvery(actions.DINNER_BOOKING_ADD_TO_BASKET, dinnerBooking.addToBasket),
    takeEvery(actions.DINNER_BOOKING_REMOVE_FROM_BASKET, dinnerBooking.removeFromBasket),
    
    takeEvery(actions.PAYMENT_INSTALLMENT_ADD_TO_BASKET, payment.addInstallmentToBasket),
    takeEvery(actions.PAYMENT_INSTALLMENT_REMOVE_FROM_BASKET, payment.removeInstallmentFromBasket),
    takeEvery(actions.PAYMENT_REMOVE_FROM_BASKET, payment.removeFromBasket),
    
    takeEvery(actions.SHOP_ITEM_SET_BASKET_QUANTITY, shopItem.setBasketQuantity),
    takeEvery(actions.SHOP_ITEM_REMOVE_FROM_BASKET, shopItem.removeFromBasket),
    
    takeEvery(actions.CREDIT_TOP_UP_ADD_TO_BASKET, creditTopUp.addToBasket),
    takeEvery(actions.CREDIT_TOP_UP_REMOVE_FROM_BASKET, creditTopUp.removeFromBasket),
    
    takeEvery(actions.BASKET_ITEMS_CLEAR, clearBasketItems),
    
    takeLatest(actions.BASKET_ITEMS_VALIDATE, validateBasketItems),
    takeLatest(actions.BASKET_ITEMS_RESOLVE_ISSUES, resolveBasketItemsIssues)
    
  ]);
}

/**
 * Clear all basket items for the given school.
 */
function* clearBasketItems(action) {
  
  const { schoolID } = action.payload;
  
  const items = yield select(getSchoolBasketItems, schoolID);
  
  for (const item of items) {
    yield put(actions.deleteBasketItem(item.type, item.basketItemID));
  }
  
}

/**
 * Check that all basket items are valid with the current entities in state
 * Will wait for entities to finish fetching before checking
 */
function* validateBasketItems() {
  
  // clear down the previous issues
  yield put(actions.setBasketItemsIssues(null));
  
  // await student data
  while (!( yield select(isStudentsReady) )) {
    yield take();
  }
  
  // silently discard basket items for dropped students
  const studentIDList = yield select(getStudentIDList);
  const basketItems = yield select(getBasketItems);
  for (const basketItem of basketItems) {
    if (!studentIDList.includes(basketItem.studentID)) {
      yield put(actions.deleteBasketItem(basketItem.type, basketItem.basketItemID));
    }
  }
  
  // get a list of issues per item type
  const issuesByItemType = yield all([
    call(clubBooking.detectBasketItemsIssues),
    call(dinnerBooking.detectBasketItemsIssues),
    call(payment.detectBasketItemsIssues),
    call(shopItem.detectBasketItemsIssues)
  ]);
  
  // flatten the issues list
  const allIssues = Immutable.List(issuesByItemType).flatten(1);
  
  // save to state
  yield put(actions.setBasketItemsIssues(allIssues));
  
}

/**
 * Fix conflicts between basket items and entities in state
 * Re-runs validation after resolution
 */
function* resolveBasketItemsIssues() {
  
  const issuesByItemType = yield select(getBasketItemsIssuesByItemType);
  
  yield all([
    call(clubBooking.resolveBasketItemsIssues, issuesByItemType.get("clubBooking")),
    call(dinnerBooking.resolveBasketItemsIssues, issuesByItemType.get("dinnerBooking")),
    call(payment.resolveBasketItemsIssues, issuesByItemType.get("payment")),
    call(shopItem.resolveBasketItemsIssues, issuesByItemType.get("shopItem"))
  ]);
  
  yield put(actions.validateBasketItems());

}



// WEBPACK FOOTER //
// ./src/state/school_money/sagas/basketSaga/index.js